import { useTranslation } from "@sagot/i18n";
import { Button, Icon, toast, Tooltip } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { usePermissions, useRouteAgentId } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { isPlanGateError, UpgradeCard } from "../shared/plan-gate";
import { isKnowledgeStorageFull, StorageFullCard } from "../shared/storage-full";
import type { SourceView } from "../shared/types";
import { useSourceEvents } from "../shared/use-source-events";
import { AddSourceDialog } from "./add-source-dialog";
import { EMPHASIS_TOKEN, Emphasize } from "./emphasize";
import { AttentionBanner, DropCallout, PipelineCard } from "./knowledge-cards";
import { KnowledgeStats, KnowledgeStatsSkeleton } from "./knowledge-stats";
import { summarize } from "./source-model";
import { ChangeAccessDialog, DeleteSourceDialog } from "./source-dialogs";
import { SourcesTable } from "./sources-table";
import { useBillingOverview } from "../shared/use-shared-queries";
import { useAgentSummary, useReindex, useReindexAll, useSources } from "./use-knowledge";
import { useUploads } from "./use-uploads";

const K = "deployKnowledge.knowledge";

function useAbilities(agentId: string | null) {
  const permissions = usePermissions(agentId);
  const writable = !permissions.readOnly;
  const can = (key: "knowledge.create" | "knowledge.edit" | "knowledge.delete" | "knowledge.reindex") =>
    writable && permissions.can(key, agentId ? { agentId } : { anywhere: true });
  // New sources answer for this page's agent when the member may edit it (the API enforces the same rule).
  const link = writable && agentId !== null && permissions.can("agents.edit", { agentId });
  return { create: can("knowledge.create"), edit: can("knowledge.edit"), remove: can("knowledge.delete"), reindex: can("knowledge.reindex"), link, readOnly: permissions.readOnly };
}

/** Disabled control + tooltip when a support session is read-only; hidden when the role lacks it. */
function Gated({ allowed, readOnly, children }: { allowed: boolean; readOnly: boolean; children: (disabled: boolean) => ReactNode }) {
  const { t } = useTranslation();
  if (allowed) return children(false);
  if (!readOnly) return null;
  return (
    <Tooltip content={t("deployKnowledge.readOnly")}>
      <span className="inline-flex">{children(true)}</span>
    </Tooltip>
  );
}

function Subtitle({ agentId }: { agentId: string | null }) {
  const { t } = useTranslation();
  const agent = useAgentSummary(agentId).data;
  return (
    <p className="mt-1 text-fg-muted">
      {t(`${K}.subtitle`)}{" "}
      {agent ? <Emphasize text={t(`${K}.subtitleAgent`, { agent: EMPHASIS_TOKEN, count: agent.stats.sources_count })} value={agent.name} /> : null}
    </p>
  );
}

/** `/w/:ws/agents/:agentId/knowledge` — workspace sources with live ingestion status. */
export function KnowledgePage() {
  const { t } = useTranslation();
  const { summary: workspace } = useWorkspaceContext();
  const agentId = useRouteAgentId();
  const message = useErrorMessage();
  const events = useSourceEvents();
  const sources = useSources(events);
  const abilities = useAbilities(agentId);
  const reindex = useReindex();
  const reindexAll = useReindexAll();
  const [adding, setAdding] = useState(false);
  const [planGated, setPlanGated] = useState(false);
  const [storageGated, setStorageGated] = useState(false);
  const meter = useBillingOverview().data?.storage_mb;
  const [accessFor, setAccessFor] = useState<SourceView | null>(null);
  const [deleting, setDeleting] = useState<SourceView | null>(null);
  const uploads = useUploads({ storageFailed: t(`${K}.add.storageFailed`), infected: t(`${K}.add.infected`) });
  const summary = sources.data ? summarize(sources.data) : null;

  const onPlanGate = (caught: unknown) => {
    if (isKnowledgeStorageFull(caught)) setStorageGated(true);
    else setPlanGated(true);
  };
  const onError = (caught: unknown) => {
    if (isPlanGateError(caught)) onPlanGate(caught);
    toast.error(message(caught));
  };
  // Full when the meter says so, or when an upload / source the member just tried was refused for it.
  const storageFull = storageGated || (meter?.limit != null && meter.used >= meter.limit) || uploads.items.some((item) => item.storageFull === true);
  const startReindex = (source: SourceView) => {
    reindex.mutate(source.id, {
      onSuccess: (result) => toast.info(result.crawl ? t(`${K}.menu.recrawlStarted`, { name: source.title }) : t(`${K}.menu.reindexStarted`)),
      onError,
    });
  };
  const startReindexAll = () => {
    reindexAll.mutate(undefined, { onSuccess: (result) => toast.info(t(`${K}.resyncStarted`, { count: result.sources.length })), onError });
  };
  const addButton = (disabled: boolean) => (
    <Button disabled={disabled} onClick={() => { setAdding(true); }}>
      <Icon name="plus" />
      {t(`${K}.addSource`)}
    </Button>
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">{t(`${K}.title`)}</h1>
          <Subtitle agentId={agentId} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Gated allowed={abilities.reindex} readOnly={abilities.readOnly}>
            {(disabled) => (
              <Button variant="secondary" disabled={disabled || !sources.data?.length} loading={reindexAll.isPending} onClick={startReindexAll}>
                <Icon name="refresh-cw" />
                {t(`${K}.resyncAll`)}
              </Button>
            )}
          </Gated>
          <Gated allowed={abilities.create} readOnly={abilities.readOnly}>
            {addButton}
          </Gated>
        </div>
      </div>
      {storageFull ? <StorageFullCard /> : null}
      {planGated ? <UpgradeCard feature={t(`${K}.title`)} /> : null}
      {summary ? <KnowledgeStats summary={summary} /> : <KnowledgeStatsSkeleton />}
      {summary ? <AttentionBanner failed={summary.attention} canRetry={abilities.reindex} onRetry={startReindex} /> : null}
      <p className="sr-only" aria-live="polite">
        {events === "polling" ? t(`${K}.live.polling`) : ""}
      </p>
      <SourcesTable
        sources={sources.data}
        isError={sources.isError}
        onRetry={() => void sources.refetch()}
        workspaceSlug={workspace.slug}
        agentId={agentId}
        can={{ reindex: abilities.reindex, edit: abilities.edit, remove: abilities.remove }}
        emptyAction={abilities.create ? addButton(false) : null}
        onReindex={startReindex}
        onChangeAccess={setAccessFor}
        onDelete={setDeleting}
      />
      {abilities.create ? <DropCallout onClick={() => { setAdding(true); }} /> : null}
      <PipelineCard />
      {adding ? (
        <AddSourceDialog open onOpenChange={setAdding} uploads={uploads} sources={sources.data ?? []} agentIds={abilities.link && agentId ? [agentId] : []} onPlanGate={onPlanGate} />
      ) : null}
      {accessFor ? <ChangeAccessDialog source={accessFor} onClose={() => { setAccessFor(null); }} /> : null}
      {deleting ? <DeleteSourceDialog source={deleting} onClose={() => { setDeleting(null); }} /> : null}
    </div>
  );
}
